import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Minus, Plus, Printer, RefreshCw, RotateCcw, X } from 'lucide-react';
import { Button } from '../Button';
import {
    PLOT_MAX_KOPIEN, PlotFehler,
    type PlotAnlageStatus, type PlotAuftrag, type PlotDokument, type PlotErreichbarkeitStufe, type PlotStatus, type PlotTon,
} from '../../services/plotter/plotTypen';
import {
    anzeigeStatus, erreichbarkeit, istOffen, kopienBegrenzen, relativeZeit, statusText,
    statusTon, uhrVersatzMs, vorschlagKopienNachdruck,
} from '../../services/plotter/plotStatus';
import {
    plotAnlageStatusLaden, plotAuftraegeLaden, plotAuftragAbbrechen, plotAuftragFinden, plotAuftragKlaeren,
    plotAuftragSenden, plotAuftragWiederholen,
} from '../../services/plotter/plotAuftraege';
import {
    meterText, plotRollenStandLaden, rollenUrteil, type PlotRollenStand, type PlotRollenUrteil,
} from '../../services/plotter/plotRolle';
import { PlotRolleErfassen } from '../admin/PlotRolleErfassen';
import {
    absichtBeginnen, absichtErfolg, absichtFehler, absichtGeprueft, absichtPruefungGescheitert, absichtVerwerfen,
    absichtenAbgleichen, absichtenAbonnieren, absichtenStand, nachdruckSchluessel, offeneFehlversuche, pruefungBeginnt,
    sendeSchluessel, type PlotSendeabsicht,
} from './plotSendeabsichten';

/**
 * „An Plotter senden“: das schon erzeugte Dokument — SiGe-Plan oder
 * Vorankündigung (`PlotDokument`) — geht als Auftrag an den Druck-PC
 * (services/plotter). Der Dialog kennt das Modul nicht: Schlüssel, Titel,
 * Dateiname und Auftragsliste bringt das Dokument mit. Der Dialog selbst
 * bucht nichts; ist ein neuer Auftrag angekommen, meldet er das
 * (`onGesendet`), und der Editor öffnet die Abrechnung. Ein Nachdruck aus der
 * Liste meldet nichts.
 *
 * Gegen Doppeldruck: Auftrags-ID und Kopienzahl stehen als Sendeabsicht
 * außerhalb des Dialogs (plotSendeabsichten.ts) und bleiben bis zur Klärung
 * stehen — auch über Schließen und Neuöffnen. Ein zweiter Klick, ein
 * Wiederholen nach einem Netzfehler oder ein neuer Dialog schicken denselben
 * Auftrag, der Server legt nichts doppelt an. Nach dem Erfolg sendet erst
 * „Weiteren Auftrag senden“ wieder. Ein hängender Auftrag wird nie von selbst
 * nachgedruckt — „Nochmal drucken“ fragt inline nach.
 *
 * Papierrolle (SQL Nr. 169/170): reicht die eingelegte Rolle nach dem Stand
 * des Servers nicht für diesen Druck, sind Senden und Nochmal drucken gesperrt;
 * Admins erfassen hier gleich eine neue Rolle. Ohne Stand (SQL fehlt, Abfrage
 * scheitert, keine Rolle erfasst) wird nicht gesperrt — der Server prüft beim
 * Anlegen ohnehin selbst.
 */

interface Props {
    blob: Blob;
    blatt: { breiteMm: number; hoeheMm: number; gekappt: boolean };
    /**
     * Was gedruckt wird. `schluessel` bündelt die Sendeabsichten (auch über
     * Schließen und Neuöffnen), `liste` bestimmt die Aufträge darunter.
     */
    dokument: PlotDokument;
    onClose: () => void;
    /**
     * Ein neuer Auftrag beginnt (erster Versuch, vor dem Hochladen) — einmal je
     * Auftrag. Ein Wiederholen, auch aus einem späteren Dialog, meldet nichts:
     * es schickt dieselbe ID mit derselben Datei.
     */
    onNeuerAuftrag?: (auftragId: string) => void;
    /**
     * Ein neuer Auftrag ist beim Server angelegt — einmal je Auftrag, auch wenn
     * der Dialog inzwischen geschlossen wurde (das Dokument geht trotzdem hinaus)
     * oder erst ein später geöffneter Dialog ihn in der Liste findet.
     */
    onGesendet?: (auftragId: string) => void;
}

/** Läuft ein Auftrag, schneller nachsehen. */
const TAKT_OFFEN_MS = 5_000;
const TAKT_RUHE_MS = 30_000;
const LISTE_LIMIT = 10;
/** Der Client kennt kein Zeitlimit: eine hängende Abfrage sperrte sonst jedes weitere Laden. */
const LADEN_FRIST_MS = 15_000;
/** Höchstens so viele Runden je Ladevorgang; ein liegengebliebener Wunsch holt der nächste Takt nach. */
const MAX_RUNDEN = 3;

const mitFrist = <T,>(p: Promise<T>): Promise<T> => new Promise<T>((ok, nein) => {
    const t = setTimeout(() => nein(new PlotFehler('netz', 'Keine Antwort vom Server. Bitte erneut aktualisieren.')), LADEN_FRIST_MS);
    p.then(w => { clearTimeout(t); ok(w); }, (e: unknown) => { clearTimeout(t); nein(e); });
});

/**
 * Solange gesendet wird, lässt sich der Dialog nicht schließen — höchstens so
 * lange. Viele schlossen sofort nach dem Klick und verließen den SiGe-Plan;
 * die Abrechnung kam dann erst beim nächsten Öffnen des Projekts. Hängt das
 * Netz, ist Schließen danach wieder frei (die Abrechnung folgt, sobald der
 * Auftrag ankommt).
 */
const SCHLIESSEN_SPERRE_MS = 10_000;

/**
 * Solange ein Sendeversuch läuft, fragt der Browser vor dem Schließen oder
 * Neuladen des Tabs nach — ein abgebrochener Versuch kann trotzdem drucken,
 * ohne dass ihn noch jemand abrechnet. Gibt das Aufräumen zurück.
 */
const warnenBeimVerlassen = (): (() => void) => {
    if (typeof window === 'undefined') return () => undefined;
    const warnen = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = '';
    };
    window.addEventListener('beforeunload', warnen);
    return () => window.removeEventListener('beforeunload', warnen);
};

const mm = (n: number) => Math.round(n).toLocaleString('de-DE');
const uhrzeit = (ms: number) => new Date(ms).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

const fehlerText = (e: unknown): string =>
    e instanceof PlotFehler || e instanceof Error ? e.message : String(e);

const TON_FARBE: Record<PlotTon, string> = {
    neutral: 'bg-slate-100 text-slate-600',
    laeuft: 'bg-blue-100 text-blue-700',
    ok: 'bg-emerald-100 text-emerald-700',
    warnung: 'bg-amber-100 text-amber-800',
    fehler: 'bg-red-100 text-red-700',
};

const STUFE_FARBE: Record<PlotErreichbarkeitStufe, { box: string; punkt: string }> = {
    erreichbar: { box: 'bg-emerald-50 border-emerald-200 text-emerald-800', punkt: 'bg-emerald-500' },
    verzoegert: { box: 'bg-amber-50 border-amber-200 text-amber-800', punkt: 'bg-amber-500' },
    nie_verbunden: { box: 'bg-amber-50 border-amber-200 text-amber-800', punkt: 'bg-amber-500' },
    kein_agent: { box: 'bg-amber-50 border-amber-200 text-amber-800', punkt: 'bg-amber-500' },
    nicht_erreichbar: { box: 'bg-red-50 border-red-200 text-red-800', punkt: 'bg-red-500' },
    nicht_eingerichtet: { box: 'bg-slate-100 border-slate-200 text-slate-700', punkt: 'bg-slate-400' },
};

/** Stufen, in denen der Auftrag erst einmal liegen bleibt. */
const WARTET_AUF_PC: readonly PlotErreichbarkeitStufe[] = ['kein_agent', 'nicht_erreichbar', 'nie_verbunden'];

/** Neue oder geänderte Zeile in die Liste: an ihrem Platz ersetzen, sonst oben einfügen. */
const zeileEinsetzen = (liste: PlotAuftrag[] | null, neu: PlotAuftrag): PlotAuftrag[] => {
    const alt = liste ?? [];
    return alt.some(a => a.id === neu.id)
        ? alt.map(a => (a.id === neu.id ? neu : a))
        : [neu, ...alt].slice(0, LISTE_LIMIT);
};

const KopienStepper: React.FC<{ wert: number; onChange: (n: number) => void; disabled?: boolean; label: string }> = ({
    wert, onChange, disabled, label,
}) => (
    <div role="group" aria-label={label} className="inline-flex items-center rounded-xl border border-slate-300 bg-white overflow-hidden">
        <button
            type="button"
            onClick={() => onChange(kopienBegrenzen(wert - 1))}
            disabled={disabled || wert <= 1}
            aria-label="Eine Kopie weniger"
            className="min-w-[44px] min-h-[44px] flex items-center justify-center text-slate-600 hover:bg-slate-100 active:bg-slate-200 disabled:opacity-30 disabled:hover:bg-transparent"
        >
            <Minus className="w-4 h-4" />
        </button>
        <span aria-live="polite" className="min-w-[3rem] text-center text-lg font-bold tabular-nums text-slate-800">{wert}</span>
        <button
            type="button"
            onClick={() => onChange(kopienBegrenzen(wert + 1))}
            disabled={disabled || wert >= PLOT_MAX_KOPIEN}
            aria-label="Eine Kopie mehr"
            className="min-w-[44px] min-h-[44px] flex items-center justify-center text-slate-600 hover:bg-slate-100 active:bg-slate-200 disabled:opacity-30 disabled:hover:bg-transparent"
        >
            <Plus className="w-4 h-4" />
        </button>
    </div>
);

/** Erklärung am gesperrten Knopf. */
const ROLLE_GESPERRT_TITEL = 'Die Papierrolle reicht für diesen Druck nicht mehr. Bitte einen Admin kontaktieren.';

/**
 * Papierrolle zum geplanten Druck: Stand, Warnung, Sperre. Admins erfassen
 * bei „reicht nicht“ und ohne Rolle gleich hier eine neue Rolle.
 */
const RollenHinweis: React.FC<{
    stand: PlotRollenStand | null;
    urteil: PlotRollenUrteil;
    hoeheMm: number;
    kopien: number;
    onGespeichert: (neu: PlotRollenStand) => void;
    /** false: das Erfassen steht schon an anderer Stelle im Dialog — nicht doppelt anbieten. */
    mitErfassen?: boolean;
}> = ({ stand, urteil, hoeheMm, kopien, onGespeichert, mitErfassen = true }) => {
    const erfassen = mitErfassen && stand?.darf_erfassen
        ? <PlotRolleErfassen stand={stand} kompakt onGespeichert={onGespeichert} />
        : null;
    if (urteil.art === 'reicht') {
        const zeile = `Papierrolle: noch ${meterText(urteil.restNachherMm + urteil.bedarfMm)} frei · `
            + `dieser Druck ${meterText(urteil.bedarfMm)} (${mm(hoeheMm)} mm × ${kopien})`;
        return urteil.knapp ? (
            <div className="p-3 rounded-xl border border-amber-200 bg-amber-50 space-y-0.5">
                <p className="flex items-start gap-1.5 text-xs font-medium text-amber-800">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    Nach diesem Druck sind noch etwa {meterText(urteil.restNachherMm)} auf der Rolle.
                </p>
                <p className="text-[11px] text-amber-700">{zeile}</p>
            </div>
        ) : (
            <p className="text-[11px] text-slate-500">{zeile}</p>
        );
    }
    if (urteil.art === 'reicht_nicht') {
        return (
            <div className="space-y-2">
                <div role="alert" className="p-3 rounded-xl border border-red-200 bg-red-50 space-y-1">
                    <p className="flex items-start gap-1.5 text-sm text-red-700">
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>
                            {`Die Papierrolle reicht für diesen Druck nicht mehr: benötigt ${meterText(urteil.bedarfMm)}, `
                                + `frei ${meterText(Math.max(0, urteil.freiMm))}. Bitte einen Admin kontaktieren; `
                                + 'er legt eine neue Rolle ein und erfasst sie in der App.'}
                        </span>
                    </p>
                    {urteil.maxKopien >= 1 && (
                        <p className="text-xs text-slate-600">
                            Mit {urteil.maxKopien} {urteil.maxKopien === 1 ? 'Kopie' : 'Kopien'} ginge es noch.
                        </p>
                    )}
                </div>
                {erfassen}
            </div>
        );
    }
    // Ohne erfasste Rolle zählt nichts gegen eine Rolle — nur Admins erfahren es.
    if (!stand || stand.rolle !== null || !erfassen) return null;
    return (
        <div className="space-y-1.5">
            <p className="text-[11px] text-slate-500">Papierrolle nicht erfasst, es wird nicht gezählt.</p>
            {erfassen}
        </div>
    );
};

/** Warum „Verwerfen“ noch gesperrt ist. */
const VerwerfenHinweis: React.FC<{ absicht: PlotSendeabsicht }> = ({ absicht }) => (
    <p className="text-[11px] text-slate-500">
        {absicht.pruefungGescheitert
            ? 'Prüfen nicht möglich. Erneut senden oder später verwerfen.'
            : 'Es wird geprüft, ob der Auftrag angekommen ist; erst dann lässt er sich verwerfen.'}
    </p>
);

type AktionArt = 'abbrechen' | 'gedruckt' | 'nicht_gedruckt' | 'nachdruck';

export const PlotterDialog: React.FC<Props> = ({ blob, blatt, dokument, onClose, onNeuerAuftrag, onGesendet }) => {
    // Sendeabsichten gehören zum Dokument, nicht zum Dialog: SiGe-Plan und
    // Vorankündigung desselben Projekts dürfen sich nicht gegenseitig auflösen.
    const schluessel = dokument.schluessel;
    // Der Listenfilter als einzelne Werte: der Aufrufer baut das Dokument je
    // Rendern neu, `laden` (useCallback) bliebe sonst nie stehen.
    const listeSige = 'sigeProjektId' in dokument.liste ? dokument.liste.sigeProjektId : null;
    const listeProjekt = 'projectId' in dokument.liste ? dokument.liste.projectId : null;
    // Für das Laden (useCallback) und für Antworten, die erst nach dem Schließen kommen.
    const gesendetRef = useRef(onGesendet);
    useEffect(() => { gesendetRef.current = onGesendet; });
    const titel = dokument.titel;

    // undefined = wird noch geladen; null = SQL Nr. 157/158 fehlt.
    const [anlage, setAnlage] = useState<PlotAnlageStatus | null | undefined>(undefined);
    const [anlageFehler, setAnlageFehler] = useState('');
    const [auftraege, setAuftraege] = useState<PlotAuftrag[] | null>(null);
    const [listeFehler, setListeFehler] = useState('');
    // Uhr für Zeitangaben und Anzeigestatus — gestellt bei jedem Laden, nicht im Rendern.
    const [jetztMs, setJetztMs] = useState(0);
    // Papierrolle: undefined = wird noch geladen; null = SQL Nr. 169/170 fehlt oder nicht abfragbar.
    const [rollenStand, setRollenStand] = useState<PlotRollenStand | null | undefined>(undefined);
    // Absichten, die der Server wegen der Papierrolle abgelehnt hat (siehe rolleAbgelehnt).
    const [rolleAbgelehntIds, setRolleAbgelehntIds] = useState<ReadonlySet<string>>(() => new Set());

    const absichten = useSyncExternalStore(absichtenAbonnieren, absichtenStand, absichtenStand);
    const sendeKey = sendeSchluessel(schluessel);
    const sendeAbsicht = absichten.get(sendeKey) ?? null;

    const [kopien, setKopien] = useState(1);
    // Dieser Dialog sendet gerade selbst (sonst: Versuch aus einem früheren Dialog).
    const [sendet, setSendet] = useState(false);
    // Schließen gesperrt: während des eigenen Sendens, höchstens SCHLIESSEN_SPERRE_MS.
    const [schliessenGesperrt, setSchliessenGesperrt] = useState(false);
    // Nummer des jüngsten eigenen Sendeversuchs: bestätigt ein Listenabgleich den
    // ersten Auftrag, bevor sein Senden endet, kann schon ein zweiter laufen —
    // Frist und Ende des ersten dürfen dessen Zustand nicht zurücksetzen.
    const sendeLaufRef = useRef(0);
    // Angekommen: Senden ruht, bis „Weiteren Auftrag senden“.
    const [gesendet, setGesendet] = useState(false);
    // Verschwindet eine laufende Absicht, ist sie angekommen (Erfolg oder in
    // der Liste gefunden) — auch, wenn ein geschlossener Dialog sie schickte.
    const [gesehen, setGesehen] = useState<PlotSendeabsicht | null>(sendeAbsicht);
    if (gesehen !== sendeAbsicht) {
        setGesehen(sendeAbsicht);
        if (gesehen?.laeuft && !sendeAbsicht) setGesendet(true);
    }

    const [aktion, setAktion] = useState<{ id: string; art: AktionArt } | null>(null);
    const [zeilenFehler, setZeilenFehler] = useState<{ id: string; text: string } | null>(null);
    const [nachdruck, setNachdruck] = useState<{ id: string; kopien: number } | null>(null);

    const aktivRef = useRef(true);
    // Der laufende Ladevorgang — ein Aufruf währenddessen wartet auf genau ihn.
    const laufRef = useRef<Promise<void> | null>(null);
    const nochmalRef = useRef(false);
    // „Aktualisieren“ von Hand läuft: Symbol dreht sich, Knopf gesperrt.
    const [manuellLaedt, setManuellLaedt] = useState(false);
    // Zeitpunkt der letzten ERFOLGREICH geladenen Liste (Anzeige „Stand“).
    const [standMs, setStandMs] = useState(0);
    // Zählt eigene Änderungen an der Liste: ein Ladevorgang, der vorher
    // begonnen hat, brächte den alten Stand zurück und wird verworfen.
    const standRef = useRef(0);
    // Ebenso für den Rollenstand: Rolle erfasst, Auftrag angelegt oder
    // geändert, Server lehnt wegen der Rolle ab.
    const rolleRef = useRef(0);
    const aktionRef = useRef(false);

    useEffect(() => {
        aktivRef.current = true;
        return () => { aktivRef.current = false; };
    }, []);

    const laden = useCallback((nachholen: boolean = true): Promise<void> => {
        // Kein zweiter Ladevorgang daneben; ein Wunsch währenddessen wird danach
        // erfüllt, und der Aufrufer wartet auf das Ende einschließlich dieser Runde.
        // Der Takt hängt nichts an (`nachholen = false`): bei langsamer Leitung
        // reihte er sonst Runde an Runde, und „Aktualisieren“ drehte endlos.
        if (laufRef.current) {
            if (nachholen) nochmalRef.current = true;
            return laufRef.current;
        }
        const lauf = (async () => {
            let runden = 0;
            do {
                runden += 1;
                nochmalRef.current = false;
                const stand = standRef.current;
                const rolleNr = rolleRef.current;
                // SiGe-Plan: seine Aufträge. Vorankündigung: die des Projekts ohne
                // SiGe-Plan — sonst stünden die Pläne des Projekts mit in der Liste.
                const filter = listeSige !== null
                    ? { sigeProjektId: listeSige, limit: LISTE_LIMIT }
                    : { projectId: listeProjekt, ohneSigePlan: true, limit: LISTE_LIMIT };
                const [status, liste, rolle] = await Promise.allSettled([
                    mitFrist(plotAnlageStatusLaden()),
                    mitFrist(plotAuftraegeLaden(filter)),
                    mitFrist(plotRollenStandLaden()),
                ]);
                // Die Sendeabsicht VOR dem Abgleich lesen — er löscht sie, gemeldet
                // wird aber ihre ID.
                const sendeKeyDok = sendeSchluessel(schluessel);
                const sendeVorher = absichtenStand().get(sendeKeyDok);
                // Offene Absichten gegen den Server: in der Liste = angekommen.
                const angekommen = liste.status === 'fulfilled'
                    ? absichtenAbgleichen(schluessel, liste.value.map(a => a.id))
                    : [];
                // Hier aufgelöst (Antwort verloren, Auftrag aber da): von hier melden,
                // nur den Sendeauftrag — ein Nachdruck wird nicht abgerechnet.
                if (sendeVorher && angekommen.includes(sendeKeyDok)) gesendetRef.current?.(sendeVorher.id);
                if (!aktivRef.current) return;
                // Nicht in der Liste heißt nichts (nur die neuesten zehn): gescheiterte
                // Versuche gezielt nachsehen. Erst „sicher nicht da“ gibt Verwerfen frei;
                // scheitert die Abfrage, beim nächsten Laden erneut.
                for (const [key, absicht] of offeneFehlversuche(schluessel)) {
                    const pruefNr = pruefungBeginnt();
                    try {
                        const gefunden = await mitFrist(plotAuftragFinden(absicht.id));
                        if (absichtGeprueft(key, absicht.id, pruefNr, gefunden !== null)) {
                            angekommen.push(key);
                            if (key === sendeKeyDok) gesendetRef.current?.(absicht.id);
                        }
                    } catch {
                        absichtPruefungGescheitert(key, absicht.id, pruefNr);
                    }
                }
                if (!aktivRef.current) return;
                if (angekommen.includes(sendeKeyDok)) setGesendet(true);
                const nunMs = Date.now();
                setJetztMs(nunMs);
                if (status.status === 'fulfilled') {
                    setAnlage(status.value);
                    setAnlageFehler('');
                } else {
                    setAnlageFehler(fehlerText(status.reason));
                }
                // Scheitert die Abfrage, sperrt das nichts: ohne Stand wird nicht
                // gesperrt, ein schon geladener bleibt bis zum nächsten stehen.
                if (rolleNr !== rolleRef.current) {
                    nochmalRef.current = true;
                } else if (rolle.status === 'fulfilled') {
                    setRollenStand(rolle.value);
                } else {
                    setRollenStand(alt => (alt === undefined ? null : alt));
                }
                if (stand !== standRef.current) {
                    nochmalRef.current = true;
                } else if (liste.status === 'fulfilled') {
                    setAuftraege(liste.value);
                    setListeFehler('');
                    setStandMs(nunMs);
                } else {
                    setListeFehler(fehlerText(liste.reason));
                }
            } while (nochmalRef.current && aktivRef.current && runden < MAX_RUNDEN);
        })().finally(() => { laufRef.current = null; });
        laufRef.current = lauf;
        return lauf;
    }, [schluessel, listeSige, listeProjekt]);

    const manuellAktualisieren = () => {
        if (manuellLaedt) return;
        setManuellLaedt(true);
        const start = Date.now();
        void laden().finally(() => {
            // Kurz sichtbar lassen — sonst wirkt der Knopf, als täte er nichts.
            const rest = Math.max(0, 600 - (Date.now() - start));
            setTimeout(() => { if (aktivRef.current) setManuellLaedt(false); }, rest);
        });
    };

    useEffect(() => { void laden(); }, [laden]);

    // Endet ein Versuch zu diesem Dokument (auch aus einem geschlossenen Dialog),
    // gleich nachladen: das Ergebnis steht dann in der Liste, und nach einem
    // Fehler prüft das Laden gezielt, ob der Auftrag doch angekommen ist.
    useEffect(() => {
        let vorher = absichtenStand();
        return absichtenAbonnieren(() => {
            const jetzt = absichtenStand();
            const beendet = [...vorher].some(([k, a]) => a.plan === schluessel && a.laeuft && !jetzt.get(k)?.laeuft);
            vorher = jetzt;
            if (beendet) void laden();
        });
    }, [schluessel, laden]);

    // Zeitstempel der Aufträge stammen von der Serveruhr: gegen eine vorgehende
    // Geräteuhr gerechnet, stünde ein laufender Druck schon nach Minuten als „unklar“.
    const serverMs = jetztMs + uhrVersatzMs(anlage ?? null);
    const offen = !!auftraege?.some(a => istOffen(anzeigeStatus(a, serverMs)));
    useEffect(() => {
        const takt = setInterval(() => { void laden(false); }, offen ? TAKT_OFFEN_MS : TAKT_RUHE_MS);
        return () => clearInterval(takt);
    }, [offen, laden]);

    useEffect(() => {
        const taste = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            e.preventDefault();
            e.stopPropagation();
            // Gesperrt: Escape verpufft, statt die Vorschau darunter zu schließen.
            if (!schliessenGesperrt) onClose();
        };
        document.addEventListener('keydown', taste, true);
        return () => document.removeEventListener('keydown', taste, true);
    }, [onClose, schliessenGesperrt]);

    const zeileUebernehmen = (neu: PlotAuftrag) => {
        standRef.current += 1;
        // Ein neuer oder geänderter Auftrag verschiebt auch den Rollenstand
        // (vorgemerkt, verbraucht) — sonst stünde für den nächsten ein falsches „reicht“.
        rolleRef.current += 1;
        setJetztMs(Date.now());
        setAuftraege(liste => zeileEinsetzen(liste, neu));
        void laden();
    };

    /**
     * Der Server lehnt wegen der Papierrolle ab (Trigger auf `plot_auftraege`):
     * der Stand hier ist veraltet — ein schon laufendes Laden verwirft ihn. Vor
     * `absichtFehler` aufrufen: dessen Ende löst das Neuladen aus (siehe oben),
     * und das holt dann den frischen Stand. Die Absicht bleibt unverändert
     * stehen; nach dem Erfassen einer neuen Rolle schickt „Erneut senden“ sie.
     * Die Absicht wird gemerkt: ihr Fehler ist dann eine klare Ablehnung, kein
     * „vielleicht angekommen“ (nur für diese Sitzung des Dialogs).
     */
    const rolleAbgelehnt = (e: unknown, absichtId: string) => {
        if (!(e instanceof PlotFehler && e.code === 'rolle_reicht_nicht')) return;
        rolleRef.current += 1;
        setRolleAbgelehntIds(alt => new Set(alt).add(absichtId));
    };

    /** Admin hat eine Rolle erfasst: der Stand gilt sofort. */
    const rolleGespeichert = (neu: PlotRollenStand) => {
        rolleRef.current += 1;
        setRollenStand(neu);
    };

    /**
     * Fehlschlag nach dem Schließen: einmal gezielt nachsehen. Hat der Server
     * den Auftrag doch angelegt (Antwort verloren), ist er angekommen und geht
     * in die Abrechnung — sonst erst, wenn jemand den Dialog wieder öffnet.
     * Wer die Absicht auflöst, meldet (wie im Laden).
     */
    const nachSchlussPruefen = async (id: string) => {
        const pruefNr = pruefungBeginnt();
        try {
            const gefunden = await mitFrist(plotAuftragFinden(id));
            if (absichtGeprueft(sendeKey, id, pruefNr, gefunden !== null)) gesendetRef.current?.(id);
        } catch {
            absichtPruefungGescheitert(sendeKey, id, pruefNr);
        }
    };

    const senden = async () => {
        if (gesendet) return;
        // Vorhandene Absicht unverändert (ID, Kopien UND Datei), sonst eine neue.
        const neuerAuftrag = !absichtenStand().has(sendeKey);
        const absicht = absichtBeginnen(sendeKey, schluessel, kopien, () => crypto.randomUUID(),
            { blob, breiteMm: blatt.breiteMm, hoeheMm: blatt.hoeheMm });
        if (!absicht) return;
        if (neuerAuftrag) onNeuerAuftrag?.(absicht.id);
        // Die Datei der Absicht: ein Wiederholen aus einem später geöffneten
        // Dialog lädt sonst das inzwischen geänderte Dokument hoch.
        const datei = absicht.datei ?? { blob, breiteMm: blatt.breiteMm, hoeheMm: blatt.hoeheMm };
        const lauf = ++sendeLaufRef.current;
        const juengster = () => aktivRef.current && sendeLaufRef.current === lauf;
        setSendet(true);
        setSchliessenGesperrt(true);
        const sperre = setTimeout(() => { if (juengster()) setSchliessenGesperrt(false); }, SCHLIESSEN_SPERRE_MS);
        const warnungAus = warnenBeimVerlassen();
        try {
            const neu = await plotAuftragSenden({
                auftragId: absicht.id,
                blob: datei.blob,
                projectId: dokument.projectId,
                sigeProjektId: dokument.sigeProjektId,
                titel,
                dateiname: dokument.dateiname,
                breiteMm: datei.breiteMm,
                hoeheMm: datei.hoeheMm,
                kopien: absicht.kopien,
            });
            // Hat ein Listenabgleich die Absicht schon aufgelöst, hat er gemeldet.
            if (absichtErfolg(sendeKey, absicht.id)) gesendetRef.current?.(absicht.id);
            if (!aktivRef.current) return;
            // Läuft schon ein neuerer Versuch, entscheidet der über „Gesendet ✓“ —
            // sonst ruhte dessen „Erneut senden“ nach einem Fehlschlag.
            if (juengster()) setGesendet(true);
            zeileUebernehmen(neu);
        } catch (e) {
            rolleAbgelehnt(e, absicht.id);
            absichtFehler(sendeKey, absicht.id, fehlerText(e));
            // Schon geschlossen: kein Dialog gleicht mehr ab (bis zum nächsten Öffnen).
            if (!aktivRef.current) void nachSchlussPruefen(absicht.id);
        } finally {
            clearTimeout(sperre);
            warnungAus();
            if (juengster()) {
                setSendet(false);
                setSchliessenGesperrt(false);
            }
        }
    };

    const sendeVerwerfen = () => {
        if (!sendeAbsicht) return;
        if (absichtVerwerfen(sendeKey, sendeAbsicht.id)) setKopien(sendeAbsicht.kopien);
    };

    /**
     * Eine Aktion an einer Zeile; true bei Erfolg. Immer nur eine zugleich.
     * `fehlerMeldung` null: der Fehler steht anderswo (Nachdruck-Absicht).
     */
    const zeilenAktion = async (
        id: string,
        art: AktionArt,
        ausfuehren: () => Promise<PlotAuftrag>,
        fehlerMeldung: ((e: unknown) => string) | null = fehlerText,
    ): Promise<boolean> => {
        if (aktionRef.current) return false;
        aktionRef.current = true;
        setAktion({ id, art });
        setZeilenFehler(null);
        try {
            const neu = await ausfuehren();
            if (aktivRef.current) zeileUebernehmen(neu);
            return true;
        } catch (e) {
            if (aktivRef.current) {
                if (fehlerMeldung) setZeilenFehler({ id, text: fehlerMeldung(e) });
                // Der Stand hat sich vermutlich geändert (abgeholt, geklärt).
                void laden();
            }
            return false;
        } finally {
            aktionRef.current = false;
            if (aktivRef.current) setAktion(null);
        }
    };

    const abbrechen = (a: PlotAuftrag) => zeilenAktion(a.id, 'abbrechen', () => plotAuftragAbbrechen(a.id), (e) =>
        e instanceof PlotFehler && e.code === 'nicht_mehr_wartend'
            ? 'Zu spät: der Druck-PC hat ihn schon abgeholt.'
            : fehlerText(e));

    const klaeren = (a: PlotAuftrag, gedruckt: boolean) =>
        zeilenAktion(a.id, gedruckt ? 'gedruckt' : 'nicht_gedruckt', () => plotAuftragKlaeren(a.id, gedruckt));

    const nachdruckOeffnen = (a: PlotAuftrag) => {
        setZeilenFehler(null);
        setNachdruck({ id: a.id, kopien: kopienBegrenzen(vorschlagKopienNachdruck(a)) });
    };

    const nachdruckSenden = async (a: PlotAuftrag, n: number) => {
        // Erst die Sperre: eine begonnene Absicht ohne Aufruf bliebe „läuft“ stehen.
        if (aktionRef.current) return;
        const key = nachdruckSchluessel(a.id);
        const absicht = absichtBeginnen(key, schluessel, n, () => crypto.randomUUID());
        if (!absicht) return;
        const ok = await zeilenAktion(a.id, 'nachdruck', async () => {
            try {
                const neu = await plotAuftragWiederholen(a, absicht.id, absicht.kopien);
                absichtErfolg(key, absicht.id);
                return neu;
            } catch (e) {
                rolleAbgelehnt(e, absicht.id);
                absichtFehler(key, absicht.id, fehlerText(e));
                throw e;
            }
        }, null);
        if (ok && aktivRef.current) setNachdruck(null);
    };

    const nachdruckVerwerfen = (a: PlotAuftrag, absicht: PlotSendeabsicht) => {
        if (absichtVerwerfen(nachdruckSchluessel(a.id), absicht.id)) setNachdruck({ id: a.id, kopien: absicht.kopien });
    };

    // Erreichbarkeit des Druck-PCs; null solange unbekannt.
    const erreich = anlage === undefined ? null : erreichbarkeit(anlage, jetztMs);
    const nichtEingerichtet = erreich?.stufe === 'nicht_eingerichtet';
    // Erst nach dem ersten Blick auf den Druck-PC senden — scheitert der, trotzdem.
    const kannSenden = !nichtEingerichtet && (anlage !== undefined || anlageFehler !== '');

    const zeigeGesendet = gesendet && !sendeAbsicht;
    const ruhendeAbsicht = sendeAbsicht && !sendeAbsicht.laeuft ? sendeAbsicht : null;

    // Papierrolle für den nächsten Druck — eine ruhende Absicht mit ihrer Anzahl.
    const sendeKopien = sendeAbsicht ? sendeAbsicht.kopien : kopien;
    const sendeUrteil = rollenStand === undefined || zeigeGesendet || sendeAbsicht?.laeuft
        ? null
        : rollenUrteil(rollenStand, blatt.hoeheMm, sendeKopien);
    const sendeRolleGesperrt = sendeUrteil?.art === 'reicht_nicht';

    return (
        <div className="fixed inset-0 z-[160] bg-black/60 backdrop-blur-sm flex items-stretch sm:items-center justify-center sm:p-4 animate-fade-in">
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="plotter-dialog-titel"
                className="bg-white w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-lg sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden"
            >
                <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-start gap-3">
                    <div className="p-2 rounded-xl bg-blue-50 text-blue-600 shrink-0"><Printer className="w-5 h-5" /></div>
                    <div className="min-w-0 flex-1">
                        <h2 id="plotter-dialog-titel" className="text-lg font-bold text-slate-800">An Plotter senden</h2>
                        <p className="text-xs text-slate-500">Blatt {mm(blatt.breiteMm)} × {mm(blatt.hoeheMm)} mm</p>
                        <p className="text-[11px] text-slate-400 truncate" title={titel}>{titel}</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={schliessenGesperrt}
                        aria-label="Schließen"
                        title={schliessenGesperrt ? 'Der Auftrag wird gesendet, gleich wieder möglich' : undefined}
                        className="min-w-[44px] min-h-[44px] flex items-center justify-center -m-1 hover:bg-slate-200 rounded-lg text-slate-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {blatt.gekappt && (
                        <div className="flex items-start gap-2 p-3 rounded-xl border border-red-200 bg-red-50 text-xs text-red-800">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            <span>Blatt länger als 5 m, unten abgeschnitten.</span>
                        </div>
                    )}

                    <div className="space-y-1.5">
                        {erreich ? (
                            <div className={`flex items-center gap-2 p-3 rounded-xl border text-sm ${STUFE_FARBE[erreich.stufe].box}`}>
                                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${STUFE_FARBE[erreich.stufe].punkt}`} aria-hidden />
                                <span className="font-medium">{erreich.text}</span>
                            </div>
                        ) : anlageFehler ? (
                            <div className="flex items-center gap-2 p-3 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-800">
                                <AlertTriangle className="w-4 h-4 shrink-0" />
                                <span>Druck-PC nicht abfragbar: {anlageFehler}</span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 p-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-500">
                                <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                                <span>Druck-PC wird geprüft …</span>
                            </div>
                        )}
                        {nichtEingerichtet && (
                            <p className="text-xs text-slate-600">
                                Plotten ist in dieser Umgebung noch nicht eingerichtet (SQL Nr. 157/158 fehlt). Bitte an den Administrator wenden.
                            </p>
                        )}
                        {((erreich && WARTET_AUF_PC.includes(erreich.stufe)) || (!erreich && anlageFehler)) && (
                            <p className="text-xs text-slate-600">Senden geht trotzdem; der Auftrag wartet, bis der Druck-PC läuft.</p>
                        )}
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <span className="text-sm font-semibold text-slate-700">Anzahl</span>
                        <KopienStepper
                            wert={sendeAbsicht ? sendeAbsicht.kopien : kopien}
                            onChange={setKopien}
                            disabled={!!sendeAbsicht || zeigeGesendet}
                            label="Anzahl der Kopien"
                        />
                    </div>

                    {/* Wegen der Rolle abgelehnt: solange sie nicht reicht, erklärt das der
                        Rollen-Hinweis darunter; danach geht „Erneut senden“ sicher durch. */}
                    {ruhendeAbsicht && rolleAbgelehntIds.has(ruhendeAbsicht.id) ? (
                        !sendeRolleGesperrt && (
                            <p role="status" className="p-3 rounded-xl border border-slate-200 bg-slate-50 text-xs text-slate-600">
                                Der Auftrag war wegen der Papierrolle abgelehnt worden. Ist eine neue Rolle erfasst, schickt „Erneut senden“ ihn.
                            </p>
                        )
                    ) : ruhendeAbsicht && (
                        <div role="alert" className="p-3 rounded-xl border border-red-200 bg-red-50 space-y-1">
                            <p className="text-sm text-red-700">{ruhendeAbsicht.fehler || 'Senden hat nicht geklappt.'}</p>
                            <p className="text-xs text-slate-600">
                                Vielleicht ist der Auftrag trotzdem angekommen. „Erneut senden“ schickt denselben Auftrag, gedruckt wird er nur einmal.
                            </p>
                        </div>
                    )}
                    {zeigeGesendet && (
                        <p role="status" className="flex items-center gap-1.5 text-sm text-emerald-700">
                            <CheckCircle2 className="w-4 h-4 shrink-0" /> Auftrag gesendet, er steht unten in der Liste.
                        </p>
                    )}
                    {sendeUrteil && (
                        <RollenHinweis
                            stand={rollenStand ?? null}
                            urteil={sendeUrteil}
                            hoeheMm={blatt.hoeheMm}
                            kopien={sendeKopien}
                            onGespeichert={rolleGespeichert}
                        />
                    )}

                    {zeigeGesendet ? (
                        <div className="flex flex-col sm:flex-row gap-2">
                            <Button variant="primary" size="lg" className="flex-1 min-h-[44px]" disabled>Gesendet ✓</Button>
                            <Button variant="secondary" size="lg" className="flex-1 min-h-[44px]" onClick={() => setGesendet(false)}
                                icon={<Plus className="w-4 h-4" />}>
                                Weiteren Auftrag senden
                            </Button>
                        </div>
                    ) : sendeAbsicht?.laeuft ? (
                        <Button variant="primary" size="lg" className="w-full min-h-[44px]" loading>
                            {sendet ? 'Wird gesendet …' : 'Wird noch gesendet …'}
                        </Button>
                    ) : ruhendeAbsicht ? (
                        <div className="space-y-1.5">
                            <div className="flex flex-col sm:flex-row gap-2">
                                <Button variant="primary" size="lg" className="flex-1 min-h-[44px]" onClick={() => { void senden(); }}
                                    disabled={!kannSenden || sendeRolleGesperrt}
                                    title={sendeRolleGesperrt ? ROLLE_GESPERRT_TITEL : undefined}
                                    icon={<RotateCcw className="w-4 h-4" />}>
                                    Erneut senden (gleicher Auftrag)
                                </Button>
                                <Button variant="secondary" size="lg" className="min-h-[44px]" onClick={sendeVerwerfen}
                                    disabled={!ruhendeAbsicht.verwerfbar}>
                                    Verwerfen
                                </Button>
                            </div>
                            {!ruhendeAbsicht.verwerfbar && <VerwerfenHinweis absicht={ruhendeAbsicht} />}
                        </div>
                    ) : (
                        <Button
                            variant="primary"
                            size="lg"
                            className="w-full min-h-[44px]"
                            onClick={() => { void senden(); }}
                            disabled={!kannSenden || sendeRolleGesperrt}
                            title={sendeRolleGesperrt ? ROLLE_GESPERRT_TITEL : undefined}
                            icon={<Printer className="w-4 h-4" />}
                        >
                            {`Senden (${kopien} ×)`}
                        </Button>
                    )}

                    {!nichtEingerichtet && (
                        <div className="pt-2 border-t border-slate-100">
                            <div className="flex items-center justify-between gap-2 mb-1">
                                <h3 className="text-sm font-bold text-slate-700">Aufträge zu diesem Dokument</h3>
                                <div className="flex items-center gap-1">
                                    {(manuellLaedt || standMs > 0) && (
                                        <span className="text-[11px] text-slate-400 tabular-nums" aria-live="polite">
                                            {manuellLaedt ? 'Wird aktualisiert …' : `Stand ${uhrzeit(standMs)}`}
                                        </span>
                                    )}
                                    <button
                                        type="button"
                                        onClick={manuellAktualisieren}
                                        disabled={manuellLaedt}
                                        aria-label="Liste aktualisieren"
                                        title="Liste aktualisieren"
                                        className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:text-blue-500 disabled:hover:bg-transparent"
                                    >
                                        <RefreshCw className={`w-4 h-4 ${manuellLaedt ? 'animate-spin' : ''}`} />
                                    </button>
                                </div>
                            </div>
                            {listeFehler && <p className="text-xs text-red-700 mb-2">Liste nicht geladen: {listeFehler}</p>}
                            {auftraege === null ? (
                                !listeFehler && (
                                    <p className="flex items-center gap-2 text-xs text-slate-400">
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Lädt …
                                    </p>
                                )
                            ) : auftraege.length === 0 ? (
                                <p className="text-xs text-slate-400">Noch keine Aufträge zu diesem Dokument.</p>
                            ) : (
                                // Eigene Bildlaufleiste: bei vielen Aufträgen bleibt das Fenster handlich.
                                <ul className="divide-y divide-slate-100 max-h-[min(18rem,40vh)] overflow-y-auto overscroll-contain pr-1 border-y border-slate-100">
                                    {auftraege.map(a => {
                                        const absicht = absichten.get(nachdruckSchluessel(a.id)) ?? null;
                                        return (
                                            <AuftragZeile
                                                key={a.id}
                                                a={a}
                                                status={anzeigeStatus(a, serverMs)}
                                                jetztMs={serverMs}
                                                aktion={aktion?.id === a.id ? aktion.art : null}
                                                gesperrt={aktion !== null}
                                                fehler={zeilenFehler?.id === a.id ? zeilenFehler.text : ''}
                                                nachdruckKopien={nachdruck?.id === a.id ? nachdruck.kopien : null}
                                                absicht={absicht}
                                                rollenStand={rollenStand}
                                                rolleAbgelehnt={absicht !== null && rolleAbgelehntIds.has(absicht.id)}
                                                rolleErfassenAnbieten={sendeUrteil?.art !== 'reicht_nicht'}
                                                onRolleGespeichert={rolleGespeichert}
                                                onAbbrechen={() => { void abbrechen(a); }}
                                                onKlaeren={(gedruckt) => { void klaeren(a, gedruckt); }}
                                                onNachdruckOeffnen={() => nachdruckOeffnen(a)}
                                                onNachdruckKopien={(n) => setNachdruck({ id: a.id, kopien: n })}
                                                onNachdruckSchliessen={() => setNachdruck(null)}
                                                onNachdruckSenden={(n) => { void nachdruckSenden(a, n); }}
                                                onNachdruckVerwerfen={() => { if (absicht) nachdruckVerwerfen(a, absicht); }}
                                            />
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

interface ZeileProps {
    a: PlotAuftrag;
    status: PlotStatus;
    jetztMs: number;
    /** Die gerade laufende Aktion an DIESER Zeile. */
    aktion: AktionArt | null;
    /** Irgendeine Aktion läuft — die übrigen Knöpfe warten. */
    gesperrt: boolean;
    fehler: string;
    /** Anzahl in der aufgeklappten Rückfrage „Nochmal drucken“; null = zu. */
    nachdruckKopien: number | null;
    /** Offene Nachdruck-Absicht zu diesem Auftrag — hält die Rückfrage offen. */
    absicht: PlotSendeabsicht | null;
    /** Papierrolle — undefined, solange noch nicht geladen. */
    rollenStand: PlotRollenStand | null | undefined;
    /** Der Server hat die Nachdruck-Absicht wegen der Papierrolle abgelehnt. */
    rolleAbgelehnt: boolean;
    /** false, wenn der Senden-Bereich das Erfassen schon anbietet. */
    rolleErfassenAnbieten: boolean;
    onRolleGespeichert: (neu: PlotRollenStand) => void;
    onAbbrechen: () => void;
    onKlaeren: (gedruckt: boolean) => void;
    onNachdruckOeffnen: () => void;
    onNachdruckKopien: (n: number) => void;
    onNachdruckSchliessen: () => void;
    onNachdruckSenden: (n: number) => void;
    onNachdruckVerwerfen: () => void;
}

const ZEILEN_KNOPF = 'min-h-[44px]';

const AuftragZeile: React.FC<ZeileProps> = ({
    a, status, jetztMs, aktion, gesperrt, fehler, nachdruckKopien, absicht, rollenStand, rolleAbgelehnt,
    rolleErfassenAnbieten, onRolleGespeichert,
    onAbbrechen, onKlaeren, onNachdruckOeffnen, onNachdruckKopien, onNachdruckSchliessen, onNachdruckSenden,
    onNachdruckVerwerfen,
}) => {
    const kannNachdrucken = status === 'unklar' || status === 'fehler' || status === 'abgelaufen'
        || status === 'abgebrochen' || status === 'gedruckt';
    const rueckfrage = absicht !== null || (kannNachdrucken && nachdruckKopien !== null);
    const teilweise = a.kopien_fertig > 0 && a.kopien_fertig < a.kopien;
    // Nachdruck gegen die Papierrolle: Blattlänge DIESES Auftrags × gewählte Anzahl.
    // „Ohne Rolle“ zeigt schon der Senden-Bereich — hier nur Stand, Warnung, Sperre.
    const nachdruckAnzahl = absicht ? absicht.kopien : nachdruckKopien ?? 1;
    const rollenUrteilNachdruck = rueckfrage && !absicht?.laeuft && rollenStand !== undefined
        ? rollenUrteil(rollenStand, a.hoehe_mm, nachdruckAnzahl)
        : null;
    const rolleGesperrt = rollenUrteilNachdruck?.art === 'reicht_nicht';
    return (
        <li className="py-3">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-xs text-slate-500">{relativeZeit(a.erstellt_am, jetztMs)}</span>
                <span className="text-sm font-semibold text-slate-700 tabular-nums">{a.kopien} ×</span>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${TON_FARBE[statusTon(status)]}`}>{statusText(status)}</span>
            </div>
            {a.meldung && <p className="text-xs text-slate-500 mt-1 break-words">{a.meldung}</p>}
            <p className="text-[11px] text-slate-400 mt-0.5">
                {a.erstellt_von_name || 'Unbekannt'}
                {a.wiederholung_von ? ' · Nachdruck' : ''}
                {teilweise ? ` · ${a.kopien_fertig} von ${a.kopien} übergeben` : ''}
            </p>

            {(status === 'wartet' || status === 'unklar' || (kannNachdrucken && !rueckfrage)) && (
                <div className="flex flex-wrap gap-2 mt-2">
                    {status === 'wartet' && (
                        <Button variant="secondary" size="sm" className={ZEILEN_KNOPF} onClick={onAbbrechen}
                            disabled={gesperrt} loading={aktion === 'abbrechen'}>
                            Abbrechen
                        </Button>
                    )}
                    {status === 'unklar' && (
                        <>
                            <Button variant="secondary" size="sm" className={ZEILEN_KNOPF} onClick={() => onKlaeren(true)}
                                disabled={gesperrt} loading={aktion === 'gedruckt'}>
                                Ist gedruckt
                            </Button>
                            <Button variant="secondary" size="sm" className={ZEILEN_KNOPF} onClick={() => onKlaeren(false)}
                                disabled={gesperrt} loading={aktion === 'nicht_gedruckt'}>
                                Nicht gedruckt
                            </Button>
                        </>
                    )}
                    {kannNachdrucken && !rueckfrage && (
                        <Button variant="ghost" size="sm" className={ZEILEN_KNOPF} onClick={onNachdruckOeffnen}
                            disabled={gesperrt} icon={<RotateCcw className="w-3.5 h-3.5" />}>
                            Nochmal drucken
                        </Button>
                    )}
                </div>
            )}

            {rueckfrage && (
                <div className="mt-2 p-3 rounded-xl border border-slate-200 bg-slate-50 space-y-3">
                    {status === 'unklar' && (
                        <p className="flex items-start gap-1.5 text-xs text-amber-800">
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                            Erst am Plotter nachsehen, sonst liegt das Blatt doppelt im Fach.
                        </p>
                    )}
                    <p className="text-[11px] text-slate-500">Gedruckt wird dieselbe Datei wie beim ersten Mal.</p>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <span className="text-xs font-semibold text-slate-600">Anzahl</span>
                        <KopienStepper
                            wert={absicht ? absicht.kopien : nachdruckKopien ?? 1}
                            onChange={onNachdruckKopien}
                            disabled={absicht !== null}
                            label="Anzahl für den Nachdruck"
                        />
                    </div>
                    {absicht && !absicht.laeuft && (rolleAbgelehnt ? (
                        !rolleGesperrt && (
                            <p role="status" className="text-xs text-slate-600">
                                Der Nachdruck war wegen der Papierrolle abgelehnt worden. Ist eine neue Rolle erfasst, schickt „Erneut senden“ ihn.
                            </p>
                        )
                    ) : (
                        <p role="alert" className="text-xs text-red-700">
                            {absicht.fehler || 'Nachdruck hat nicht geklappt.'} Erneut senden schickt denselben Auftrag, gedruckt wird er nur einmal.
                        </p>
                    ))}
                    {rollenUrteilNachdruck && rollenUrteilNachdruck.art !== 'ohne_rolle' && (
                        <RollenHinweis
                            stand={rollenStand ?? null}
                            urteil={rollenUrteilNachdruck}
                            hoeheMm={a.hoehe_mm}
                            kopien={nachdruckAnzahl}
                            onGespeichert={onRolleGespeichert}
                            mitErfassen={rolleErfassenAnbieten}
                        />
                    )}
                    <div className="flex flex-wrap justify-end gap-2">
                        {absicht?.laeuft ? (
                            <Button variant="primary" size="sm" className={ZEILEN_KNOPF} loading>
                                {aktion === 'nachdruck' ? 'Wird gesendet …' : 'Wird noch gesendet …'}
                            </Button>
                        ) : absicht ? (
                            <>
                                <Button variant="ghost" size="sm" className={ZEILEN_KNOPF} onClick={onNachdruckVerwerfen}
                                    disabled={!absicht.verwerfbar}>
                                    Verwerfen
                                </Button>
                                <Button variant="primary" size="sm" className={ZEILEN_KNOPF} onClick={() => onNachdruckSenden(absicht.kopien)}
                                    disabled={gesperrt || rolleGesperrt} title={rolleGesperrt ? ROLLE_GESPERRT_TITEL : undefined}
                                    icon={<RotateCcw className="w-3.5 h-3.5" />}>
                                    Erneut senden (gleicher Auftrag)
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button variant="ghost" size="sm" className={ZEILEN_KNOPF} onClick={onNachdruckSchliessen}>
                                    Abbrechen
                                </Button>
                                <Button variant="primary" size="sm" className={ZEILEN_KNOPF} onClick={() => onNachdruckSenden(nachdruckKopien ?? 1)}
                                    disabled={gesperrt || rolleGesperrt} title={rolleGesperrt ? ROLLE_GESPERRT_TITEL : undefined}
                                    icon={<Printer className="w-3.5 h-3.5" />}>
                                    Jetzt nochmal drucken
                                </Button>
                            </>
                        )}
                    </div>
                    {absicht && !absicht.laeuft && !absicht.verwerfbar && <VerwerfenHinweis absicht={absicht} />}
                </div>
            )}

            {fehler && <p role="alert" className="text-xs text-red-700 mt-2">{fehler}</p>}
        </li>
    );
};
